import { getRouteApi } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import {
    ALPHA_2_ISO_3166,
    ALPHA_2_ISO_3166_EU,
    LIMITED_RESIDENCE_PERMIT_TYPES,
    LOAN_REQUEST_COMMANDS,
    RESIDENCE_PERMIT_TYPES,
    SALUTATIONS,
} from 'cpool-constants';
import { type ValueOf } from 'cpool-utils';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BsArrow90DegLeft } from 'react-icons/bs';

import AufenthaltBefristetBis from './aufenthalt/AufenthaltBefristetBis.tsx';
import Aufenthaltstitel from './aufenthalt/Aufenthaltstitel.tsx';
import InDeutschlandSeit from './aufenthalt/InDeutschlandSeit.tsx';
import { ErrorMessage } from '../General/ErrorMessage.tsx';
import SchufaField from '../General/SchufaField.tsx';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { FormDateInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDateInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { FormTextInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormTextInput';
import { useCountryOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useCanViewSchufa } from '~/features/leadDetails/hooks/useCanViewSchufa';
import { useSchufaTransfer } from '~/features/leadDetails/hooks/useSchufaTransfer.ts';
import type { SchemaPersonal } from '~/features/leadDetails/schemas/personal.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type PersonalDetailsProps = {
    applicant: 1 | 2;
    applicantId?: string;
};

const PersonalDetails: React.FC<PersonalDetailsProps> = ({ applicant, applicantId }) => {
    const { t } = useTranslation();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const canViewSchufa = useCanViewSchufa(leadId);
    const { control, setValue } = useFormContext<SchemaPersonal>();
    const countryOptions = useCountryOptions();
    const staatsangehoerigkeit = useWatch<SchemaPersonal>({
        name: `antragsteller${applicant}.personendaten.staatsangehoerigkeit`,
    }) as ValueOf<typeof ALPHA_2_ISO_3166>;
    const aufenthaltstitel = useWatch<SchemaPersonal>({
        name: `antragsteller${applicant}.personendaten.aufenthaltstitel`,
    }) as ValueOf<typeof RESIDENCE_PERMIT_TYPES>;
    const isNameDisabled = !canExecuteApplicantCommand({
        applicant,
        commandName: LOAN_REQUEST_COMMANDS.changeApplicantName,
        permissions,
    });
    const isBirthDataDisabled = !canExecuteApplicantCommand({
        applicant,
        commandName: LOAN_REQUEST_COMMANDS.updateApplicantBirthData,
        permissions,
    });
    const isResidenceDataDisabled = !canExecuteApplicantCommand({
        applicant,
        commandName: LOAN_REQUEST_COMMANDS.updateApplicantResidenceData,
        permissions,
    });

    const genderOptions = [SALUTATIONS.HERR, SALUTATIONS.FRAU, SALUTATIONS.DIVERS].map((value) => ({
        label: t(`gender.${value}`),
        value,
    }));

    const {
        schufaBirthdate,
        schufaBirthplace,
        schufaFirstname,
        schufaGender,
        schufaLastname,
        transferBirthdate,
        transferBirthplace,
        transferFirstname,
        transferGender,
        transferLastname,
        transferPersonal,
    } = useSchufaTransfer({ antragsteller: applicant, applicantId, setValue });

    return (
        <FormCard className="flex-row gap-3">
            <div className="space-y-2 flex-1">
                <div className="h-8"></div>
                <FormDropdown
                    control={control}
                    id={`antragsteller${applicant}.personendaten.anrede`}
                    label={t('PERSONAL_DATA.SALUTATIONS')}
                    options={genderOptions}
                    searchable={false}
                    disabled={isNameDisabled}
                />
                <FormTextInput
                    control={control}
                    id={`antragsteller${applicant}.personendaten.vorname`}
                    label={t('PERSONAL_DATA.FIRSTNAME')}
                    disabled={isNameDisabled}
                />
                <FormTextInput
                    control={control}
                    id={`antragsteller${applicant}.personendaten.nachname`}
                    label={t('PERSONAL_DATA.LASTNAME')}
                    disabled={isNameDisabled}
                />
                <FormTextInput
                    control={control}
                    id={`antragsteller${applicant}.personendaten.geburtsname`}
                    label={t('PERSONAL_DATA.BIRTH_NAME')}
                    disabled={isBirthDataDisabled}
                />
                <FormDateInput
                    control={control}
                    id={`antragsteller${applicant}.personendaten.geburtsdatum`}
                    label={t('PERSONAL_DATA.BIRTHDATE')}
                    disabled={isBirthDataDisabled}
                />
                <FormTextInput
                    control={control}
                    id={`antragsteller${applicant}.personendaten.geburtsort`}
                    label={t('PERSONAL_DATA.BIRTHPLACE')}
                    disabled={isBirthDataDisabled}
                />
                <FormDropdown
                    control={control}
                    id={`antragsteller${applicant}.personendaten.geburtsland`}
                    label={t('PERSONAL_DATA.COUNTRY_OF_BIRTH')}
                    options={countryOptions}
                    disabled={isBirthDataDisabled}
                />
                <FormDropdown
                    control={control}
                    id={`antragsteller${applicant}.personendaten.staatsangehoerigkeit`}
                    label={t('PERSONAL_DATA.NATIONALITY')}
                    options={countryOptions}
                    disabled={isResidenceDataDisabled}
                />
                <ErrorMessage
                    name={`antragsteller${applicant}.personendaten.staatsangehoerigkeit`}
                    htmlFor={`antragsteller${applicant}.personendaten.staatsangehoerigkeit`}
                />
                {staatsangehoerigkeit !== ALPHA_2_ISO_3166.DE && <InDeutschlandSeit antragsteller={applicant} />}
                {!(Object.values(ALPHA_2_ISO_3166_EU) as string[]).includes(staatsangehoerigkeit) && (
                    <Aufenthaltstitel antragsteller={applicant} />
                )}
                {(
                    [
                        ...LIMITED_RESIDENCE_PERMIT_TYPES,
                        RESIDENCE_PERMIT_TYPES.RESIDENCE_PERMIT_SWITZERLAND,
                    ] as unknown as string[]
                ).includes(aufenthaltstitel) && <AufenthaltBefristetBis antragsteller={applicant} />}
            </div>
            {canViewSchufa && (
                <div className="space-y-2 flex-1">
                    <div className="flex justify-between items-center">
                        <span className="italic text-sm font-normal text-midnight">
                            {t('PERSONAL_DATA.DATA_FROM_SCHUFA')}
                        </span>
                        <Button
                            type="button"
                            variant={ButtonVariant.SECONDARY}
                            onClick={transferPersonal}
                            disabled={isNameDisabled || isBirthDataDisabled}
                            iconLeft={<BsArrow90DegLeft strokeWidth={1} size={15} />}
                        >
                            {t('PERSONAL_DATA.TAKE_OVER_ALL_SCHUFA_DATA')}
                        </Button>
                    </div>

                    <SchufaField displayValue={schufaGender} disabled={isNameDisabled} onTransfer={transferGender} />
                    <SchufaField
                        displayValue={schufaFirstname}
                        disabled={isNameDisabled}
                        onTransfer={transferFirstname}
                    />
                    <SchufaField
                        displayValue={schufaLastname}
                        disabled={isNameDisabled}
                        onTransfer={transferLastname}
                    />
                    <div className="h-8"></div>
                    <SchufaField
                        displayValue={schufaBirthdate}
                        disabled={isBirthDataDisabled}
                        onTransfer={transferBirthdate}
                    />
                    <SchufaField
                        displayValue={schufaBirthplace}
                        disabled={isBirthDataDisabled}
                        onTransfer={transferBirthplace}
                    />
                </div>
            )}
        </FormCard>
    );
};

export default PersonalDetails;
